<script lang="ts" setup>
import { UserAvatar } from "@/components";
import {
  DocumentSpace,
  useSearchUsersWithinTenantQuery,
  User,
} from "@/graphql/operations";
import {
  PropType,
  ref,
  computed,
  watch,
  reactive,
  onMounted,
  nextTick,
} from "vue";
import { useStore } from "vuex";

const store = useStore();

const inputElem = ref<HTMLInputElement>();
const clearButton = ref<HTMLButtonElement>();
const activationButton = ref<HTMLButtonElement>();

const props = defineProps({
  documentSpace: {
    type: Object as PropType<DocumentSpace>,
    required: true,
  },
  active: {
    type: Boolean,
    required: true,
  },
});

const emits = defineEmits(["stateChange", "change"]);

const filter = ref<string>("");

const { data } = useSearchUsersWithinTenantQuery({
  variables: reactive({
    search: filter,
    tenantId: store.getters.user.tenantId,
    spaceId: props.documentSpace.id,
  }),
  requestPolicy: "network-only",
});

const searchResult = computed(() => data.value?.usersByFlags?.nodes);
const highlightedUserId = ref<string | null>(null);
const listboxId = computed(
  () => `access-user-search-results-${props.documentSpace.id}`,
);
const selectableUsers = computed(() =>
  (searchResult.value ?? []).filter(
    (user) => user?.documentSpaceAccesses?.nodes?.length === 0,
  ),
);
const activeDescendantId = computed(() =>
  highlightedUserId.value
    ? `${listboxId.value}-user-${highlightedUserId.value}`
    : undefined,
);

const clearHighlight = () => {
  highlightedUserId.value = null;
};

const isSelectable = (
  user: NonNullable<NonNullable<typeof searchResult.value>[number]>,
) => user.documentSpaceAccesses?.nodes?.length === 0;

const setSelectedUser = (user: User) => {
  selectedUser.value = user;
};

const selectSearchResult = (
  user: NonNullable<NonNullable<typeof searchResult.value>[number]>,
) => {
  if (isSelectable(user)) {
    setSelectedUser(user as User);
  }
};

const moveHighlight = (direction: 1 | -1) => {
  const users = selectableUsers.value;
  if (users.length === 0) {
    clearHighlight();
    return;
  }

  const currentIndex = users.findIndex(
    (user) => user?.id === highlightedUserId.value,
  );
  const nextIndex =
    currentIndex === -1
      ? direction === 1
        ? 0
        : users.length - 1
      : (currentIndex + direction + users.length) % users.length;

  highlightedUserId.value = users[nextIndex]!.id;
};

function handleKeyEvent(ev: KeyboardEvent) {
  if (ev.key === "ArrowDown") {
    ev.preventDefault();
    moveHighlight(1);
  } else if (ev.key === "ArrowUp") {
    ev.preventDefault();
    moveHighlight(-1);
  } else if (ev.key === "Enter") {
    ev.preventDefault();
    const highlightedUser = selectableUsers.value.find(
      (user) => user?.id === highlightedUserId.value,
    );
    if (highlightedUser) {
      selectSearchResult(highlightedUser);
    }
  }
}

const selectedUser = ref<User | null>(null);

watch(filter, clearHighlight);
watch(searchResult, clearHighlight);

watch(
  () => props.active,
  (active) => {
    clearHighlight();
    if (active) {
      return;
    }

    filter.value = "";
    selectedUser.value = null;
  },
);

watch(selectedUser, async (selected, previous) => {
  clearHighlight();
  emits("change", selectedUser.value);
  await nextTick();

  if (selected) {
    clearButton.value?.focus();
  } else if (previous && props.active) {
    inputElem.value?.focus();
  }
});

onMounted(() => {
  if (props.active && inputElem.value) {
    inputElem.value.focus();
  }
});

defineExpose({
  focusActivation: () => activationButton.value?.focus(),
});
</script>

<template>
  <div class="search_container">
    <template v-if="!selectedUser">
      <div class="search_input">
        <button
          v-if="!active"
          type="button"
          ref="activationButton"
          class="search search_activation"
          data-test="activate-access-user-search"
          @click="emits('stateChange', true)"
        >
          {{ $t("documentSpace.access.userSearch.placeholder") }}
        </button>
        <form v-else class="search" @submit.prevent>
          <input
            class="input"
            type="text"
            ref="inputElem"
            :placeholder="$t('documentSpace.access.userSearch.placeholder')"
            v-model="filter"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="active && !selectedUser"
            :aria-controls="listboxId"
            :aria-activedescendant="activeDescendantId"
            @keydown="handleKeyEvent"
          />
        </form>
      </div>

      <div
        class="search_results"
        v-if="active && !selectedUser"
        :id="listboxId"
        role="listbox"
      >
        <template v-if="searchResult">
          <div
            v-for="user in searchResult"
            :key="user!.id"
            :id="`${listboxId}-user-${user!.id}`"
            role="option"
            :aria-disabled="!isSelectable(user!)"
            :aria-selected="highlightedUserId === user!.id"
            :class="{
              row: true,
              clickable: isSelectable(user!),
              disabled: !isSelectable(user!),
              highlighted: highlightedUserId === user!.id,
            }"
            @click="selectSearchResult(user!)"
          >
            <div class="left">
              <user-avatar
                :appearance="'subtle'"
                :name="user!.displayName!"
                :email="user!.email!"
              />
            </div>
            <div>
              <div class="identity">{{ user!.displayName }}</div>
              <div class="identity">{{ user!.email }}</div>
              <div v-if="!isSelectable(user!)" class="access_status">
                {{
                  $t(
                    user!.documentSpaceAccesses?.nodes
                      ? "documentSpace.access.userSearch.alreadyHasAccess"
                      : "documentSpace.access.userSearch.accessStatusUnavailable",
                  )
                }}
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          {{ $t("documentSpace.access.userSearch.empty") }}
        </template>
      </div>
    </template>

    <template v-else>
      <div class="search_input">
        <div class="outer input">
          <div class="selected">
            <user-avatar
              class="small"
              :appearance="'subtle'"
              :name="selectedUser.displayName!"
              :email="selectedUser.email!"
            />
            {{ selectedUser.displayName }}
            <button
              type="button"
              ref="clearButton"
              class="icon_button material-symbols-outlined"
              :aria-label="$t('cancel')"
              @click="selectedUser = null"
            >
              close
            </button>
          </div>
        </div>

        <slot name="settings"></slot>
      </div>

      <p class="info">
        {{ $t("documentSpace.access.invite.info") }}
      </p>
    </template>
  </div>
</template>

<style scoped lang="scss">
.search_container {
  position: relative;
}

.search_container .search_results {
  position: absolute;
  top: 38px;
  left: 0;
  right: 0;
  background-color: var(--classic-surface-raised, #f5f9fb);
  z-index: 100;
}

.search_results .row {
  padding: 0.6em 0.8em;
  display: flex;
  align-items: center;
}

.row .left {
  margin-right: 1em;
}

.search_results .row.clickable:hover,
.search_results .row.highlighted {
  cursor: pointer;
  background-color: var(--classic-selection, #e3eef3);
}

.search_results .row.disabled {
  pointer-events: none;
}

.row.disabled .left,
.row.disabled .identity {
  opacity: 0.5;
}

.access_status {
  margin-top: 0.2em;
  font-size: 0.9em;
  color: var(--classic-text, #37352f);
}

.search {
  display: block;
  overflow: visible;
  border: 1px solid var(--classic-border-strong, #bbb);
  border-radius: 3px;
  background-color: var(--classic-surface, #fff);
  height: 35px;
  width: 100%;
}

.search_activation {
  box-sizing: border-box;
  padding: 0 0.8em;
  color: var(--classic-text-muted, #9b9b9b);
  font: inherit;
  text-align: left;
  cursor: pointer;
}

.input {
  margin-left: 3px;
  width: calc(100% - 3px);
  min-width: 300px;
  border-radius: 0px;
  box-shadow: inset 0 0.0625em 0.125em rgba(10, 10, 10, 0.05);
}

.search .input {
  border: none;
  width: calc(100% - 3px);
}

.input:active,
.input:focus {
  border: none;
  background: transparent;
  outline: none;
  box-shadow: none;
}

.outer.input {
  padding: 18px 10px;
  height: 36px;
  border-color: var(--classic-border-strong, #b5b5b5);
  border-radius: 3px;
  display: flex;
}

.search_input {
  display: flex;
}

.selected {
  font-size: 1em;
  display: flex;
  align-items: center;
  background: var(--classic-tag-surface, rgba(206, 205, 202, 0.5));
  padding: 2px 6px;
  border-radius: 4px;
}

.selected .avatar {
  font-size: 0.7em;
  margin-right: 0.5em;
}

.selected .material-symbols-outlined {
  font-size: 0.9em;
  padding: 5px 5px 3px 8px;
  color: var(--classic-text, rgb(55, 53, 47));
  cursor: pointer;
}

.icon_button {
  border: 0;
  background: transparent;
  margin-left: 0;
}
</style>
